import { User } from '@chegg-wt/core/types/auth'
import { ClientState } from '@chegg-wt/core/types/site'
import { mapJwtEmailToUser } from '@chegg-wt/core/utils/token'
import { trackWithoutThrow } from './errors'
import { isWindowDefined } from './isWindowDefined'

export const LOCAL_STORAGE_KEYS = {
  clientState: 'clientState',
  user: 'user',
  // ideally we should use the same local storage key for the token,
  // but previously the upload widget only shared a token on WFE
  uploadWidgetToken: 'wfeToken',
  dashboardSeenTour: 'dashboardSeenTour',
  guestProject: 'guestProject',
  visitedFolder: 'visited_folder',
}

export const setLocalStorageItem = (key: string, value: string) => {
  if (isWindowDefined) {
    try {
      window.localStorage.setItem(key, value)
    } catch (e) {
      return undefined
    }
  }
}

export const getLocalStorageItem = (key: string): string | null => {
  if (isWindowDefined) {
    return window.localStorage.getItem(key)
  }

  return null
}

export const removeLocalStorageItem = (key: string) => {
  if (isWindowDefined) {
    window.localStorage.removeItem(key)
  }
}

const getLocalBibInfo = (): ClientState => {
  const emptyClientState = {} as ClientState
  const localStorageString = getLocalStorageItem(LOCAL_STORAGE_KEYS.clientState)

  if (localStorageString) {
    // Only parse localstorage if it is not undefined or null;
    return JSON.parse(localStorageString) || emptyClientState
  }

  return emptyClientState
}

const getUserBibInfo = (): User => {
  try {
    return JSON.parse(getLocalStorageItem(LOCAL_STORAGE_KEYS.user))
  } catch (e) {
    trackWithoutThrow(e)
    return null
  }
}

export const updateUserAndToken = (userAndToken: User) => {
  setLocalStorageItem(
    LOCAL_STORAGE_KEYS.user,
    JSON.stringify({
      ...getUserBibInfo(),
      ...mapJwtEmailToUser(userAndToken),
    })
  )

  setLocalStorageItem(LOCAL_STORAGE_KEYS.uploadWidgetToken, userAndToken.jwt)
}

export const getToken = (): string | null => getUserBibInfo()?.jwt || null

export const deleteLocal = (key: string) => {
  const local = getLocalBibInfo()
  delete local[key]

  setLocalStorageItem(LOCAL_STORAGE_KEYS.clientState, JSON.stringify(local))
  return true
}

const setEbUser = (user: User) => {
  setLocalStorageItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(user))
  setLocalStorageItem(LOCAL_STORAGE_KEYS.uploadWidgetToken, user.jwt)
}

const setLocal = <T extends keyof ClientState>(name: T, val: ClientState[T]) => {
  const local = getLocalBibInfo()
  local[name] = val

  setLocalStorageItem(LOCAL_STORAGE_KEYS.clientState, JSON.stringify(local))
  return true
}

export const deleteUser = () => {
  removeLocalStorageItem(LOCAL_STORAGE_KEYS.user)
  removeLocalStorageItem(LOCAL_STORAGE_KEYS.uploadWidgetToken)
}

export const getDashboardTourSeen = (): boolean =>
  getLocalStorageItem(LOCAL_STORAGE_KEYS.dashboardSeenTour) === 'true'
export const setDashboardTourSeen = () =>
  setLocalStorageItem(LOCAL_STORAGE_KEYS.dashboardSeenTour, 'true')

export const setGuestProjectId = (sessionId, projectId) => {
  const payload = JSON.stringify({ sessionId, projectId })
  setLocalStorageItem(LOCAL_STORAGE_KEYS.guestProject, payload)
}

export { getLocalBibInfo, getUserBibInfo, setEbUser, setLocal }
